import {
  parseMoneyToCents,
  roundToCashStep,
  tt,
  formatCents,
  formatDecimal,
  type MoneyInput,
} from '@agos/shared'

/**
 * What is still owed on the lines being paid - `FRONTEND.md` §9.453, `BACKEND.md` §10A.216, FB-776.
 *
 * **The payment dialog measured every bill as the sum of its lines.** On an order that already
 * carried a zľava - given in the order builder, or by a coupon, or by points - that is the gross, and
 * the dialog printed it, asked *Prebehla platba úspešne?* about it and computed its own percentage
 * from it. Two ways to lose money, in opposite directions:
 *
 * - nothing typed at the close: the customer was invoiced the **full** price for a discounted table
 * - the same percentage typed again, because the box was empty: the screen said **half** and the
 *   order was recorded **free**
 *
 * The owner's words for the second one are the proof it was the row that was wrong and not the till:
 * *"there are a lot orders like that even tho they paid half the price - it was never free for
 * them"*.
 *
 * **Deliberately the server's own expression.** `SplitPaymentService.shareOfDiscount` is
 * `discount x paid / subtotal` rounded to the five-cent coin, and the final portion adds the order's
 * discount exactly; both are mirrored here so the document, the confirmation question and the row the
 * server writes agree to the cent.
 */

/** An order as this dialog reads it: the money columns, in euros as the API sends them. */
export interface BilledOrder {
  subtotal?: MoneyInput | null
  discount?: MoneyInput | null
}

/**
 * The share of an order's existing discount that belongs to the lines being paid.
 *
 * Rounded to the five-cent step **only when it is a share**, which is `Money.payable` against
 * `Money.cents`: a whole bill takes the discount as recorded, because that is the figure
 * `closeFinalPortion` adds to. Rounding a whole discount to the coin would put this up to two cents
 * from the server.
 */
export function existingDiscountCents(order: BilledOrder, paidCents: number): number {
  const subtotal = parseMoneyToCents(order.subtotal ?? 0)
  const discount = parseMoneyToCents(order.discount ?? 0)
  if (discount <= 0 || subtotal <= 0) return 0
  if (paidCents >= subtotal) return discount
  return roundToCashStep((discount * paidCents) / subtotal)
}

/**
 * The bill: the lines being paid, less the discount already on them.
 *
 * Never below zero - a discount bigger than the portion does not mean the restaurant owes money,
 * which is `Money.notNegative` on the other side of the wire.
 */
export function outstandingCents(order: BilledOrder, paidCents: number): number {
  return Math.max(0, paidCents - existingDiscountCents(order, paidCents))
}

/**
 * The line that stops the second mistake being made at all.
 *
 * §9.431 fixed this class of thing in the order editor by **seeding** the discount box. Here that
 * would be wrong - the zľava is not being given again - so the screen states what is already off
 * instead, above a box that now asks for a *further* one.
 */
export function existingDiscountNote(order: BilledOrder, paidCents: number): string | null {
  const already = existingDiscountCents(order, paidCents)
  if (already <= 0) return null
  return `${tt('Zľava už na objednávke')} ${formatCents(already)} - ${tt('zostáva')} ${formatCents(
    outstandingCents(order, paidCents),
  )}`
}

/** One line of the bill: what it is called and what it comes to, in cents. */
export interface BillRow {
  label: string
  cents: number
  /** A reduction, so the screen can draw it with its sign and its own colour. */
  off: boolean
}

/**
 * The bill, itemised - the owner's *"in zaplatit fix that also price with zlava will be shown"*.
 *
 * **Drawn only where there is something to break down.** On an ordinary bill - no zľava on the
 * order, none typed - the single *Na zaplatenie* line already says everything and a two-row table
 * repeating it is noise. The moment either discount exists the operator needs to see which is
 * which, because that is how the second one stops being typed by mistake.
 *
 * `payable` is deliberately **not** included: it is `payableCents` on the screen already, and one
 * figure computed in two places is how the document and the row came to disagree in the first place.
 */
export function billRows(
  order: BilledOrder,
  paidCents: number,
  furtherDiscountCents: number,
): BillRow[] {
  const already = existingDiscountCents(order, paidCents)
  if (already <= 0 && furtherDiscountCents <= 0) return []

  const rows: BillRow[] = [{ label: tt('Cena bez zľavy'), cents: paidCents, off: false }]
  if (already > 0) {
    rows.push({ label: tt('Zľava na objednávke'), cents: already, off: true })
  }
  if (furtherDiscountCents > 0) {
    rows.push({ label: tt('Ďalšia zľava'), cents: furtherDiscountCents, off: true })
  }
  return rows
}

/**
 * The order as the dialog should price it, after the operator has edited its zľava - §9.464.
 *
 * **The owner asked for this** (`OWNER-QUESTIONS.md` #64): *"Yes - let them edit it there"*. The
 * dialog already **shows** what is off (§9.453) and its own box adds a *further* discount; this makes
 * the original editable without leaving the screen.
 *
 * **A local override, and it clears itself.** `PATCH /orders/{id}` stores the new figure and
 * `recomputeTotals` re-derives the total, but the dialog is handed its order as a prop and its two
 * callers refresh differently - one reloads the list, the other navigates away. So the dialog holds the
 * figure it just saved and prices from it, and **drops the override the moment the prop agrees**: a
 * local value that outlived the server's is the shape of a money bug, and the poll brings the real one
 * within eight seconds.
 */
export function pricedWith(order: BilledOrder, savedDiscountCents: number | null): BilledOrder {
  if (savedDiscountCents === null) return order
  if (parseMoneyToCents(order.discount ?? 0) === savedDiscountCents) return order
  return { ...order, discount: savedDiscountCents / 100 }
}

/**
 * Whether the override has served its purpose and should be forgotten.
 *
 * Separate from {@link pricedWith} so a caller can clear its state rather than carry a value that no
 * longer changes anything - a stale flag nobody reads is how a screen ends up with two sources of one
 * figure.
 */
export function overrideIsSpent(order: BilledOrder, savedDiscountCents: number | null): boolean {
  return savedDiscountCents !== null && parseMoneyToCents(order.discount ?? 0) === savedDiscountCents
}

/**
 * The payload that changes an order's own zľava - §9.464.
 *
 * **The same four fields `editScalars` sends**, and for the same reason §9.420 gives: `discount` is the
 * whole figure in euros and the three `manualDiscount*` fields record *who* gave it and how it was
 * typed. Diverging here would put a reduction typed at the till into the coupon bucket of the owner's
 * discount report, which is what §10A.133 exists to prevent.
 *
 * **Always `fixed`**, because this box takes euros. The dialog's *further* discount box has its own
 * percent/euro toggle; this one is editing a figure the operator can see, so asking them to re-derive a
 * percentage from it would invite exactly the double-count FB-776 was.
 */
export function editedDiscountPayload(
  euros: string,
  operatorName: string | null,
): Record<string, unknown> {
  const cents = parseMoneyToCents(euros)
  if (cents <= 0) {
    // Cleared. `present-and-null means clear it` is the server's own documented rule, so the operator
    // goes with the discount rather than outliving it - §9.420's wording, and its shape.
    return { discount: 0, manualDiscountType: null, manualDiscountValue: null, manualDiscountOperator: null }
  }
  return {
    discount: cents / 100,
    manualDiscountType: 'fixed',
    manualDiscountValue: cents / 100,
    manualDiscountOperator: operatorName,
  }
}

/**
 * The order's own zľava as an editable string - §9.464.
 *
 * **The order's whole discount, not the share.** {@link existingDiscountCents} answers what belongs to
 * the portion being paid, which is the right figure to *show* on a split; it is the wrong figure to
 * *edit*, because the column being written is the order's. The dialog only offers the edit on a whole
 * bill for exactly that reason, and this helper is what the two would disagree about if it ever stopped.
 *
 * **A comma, because the operator reads it before they retype it.** `formatDecimal` rather than
 * `toFixed` - N-02, and the lint rule caught this exact line. `parseMoneyToCents` replaces the comma
 * on the way back, so either separator is accepted from the keyboard.
 */
export function discountDraftFor(order: BilledOrder): string {
  const cents = parseMoneyToCents(order.discount ?? 0)
  return cents <= 0 ? '' : formatDecimal(cents / 100)
}
